/**
 * Product Page Auto-Scroll and Header Behavior
 * 
 * Features:
 * - Auto-scrolls to product content section on page load
 * - Hides header initially on product pages
 * - Shows header when user scrolls up
 */

import { onDocumentLoaded } from '@theme/utilities';

class ProductPageScroll {
  constructor() {
    this.header = null;
    this.headerGroup = null;
    this.lastScrollTop = 0;
    this.isScrollingUp = false;
    this.hasScrolled = false;
    this.scrollThreshold = 50; // Minimum scroll distance before showing header
    this.init();
  }

  init() {
    // Only run on product pages
    if (document.body.classList.contains('template-product') || window.location.pathname.includes('/products/')) {
      onDocumentLoaded(() => {
        this.setupHeader();
        this.setupAutoScroll();
        this.setupScrollListener();
      });
    }
  }

  setupHeader() {
    this.header = document.querySelector('#header-component');
    this.headerGroup = document.querySelector('#header-group');
    const headerSection = document.querySelector('.header-section');
    
    if (this.header) {
      // Initially hide the header on product pages
      this.header.setAttribute('data-product-page-hidden', '');
      this.header.dataset.stickyState = 'idle';
      
      // Ensure header has sticky behavior enabled
      if (!this.header.hasAttribute('sticky')) {
        this.header.setAttribute('sticky', 'scroll-up');
      }
      
      // Ensure header section is positioned correctly
      if (headerSection) {
        headerSection.style.position = 'sticky';
        headerSection.style.top = '-1px';
        headerSection.style.zIndex = 'var(--layer-sticky, 100)';
      }
    }
  }

  setupAutoScroll() {
    // Find the product information section
    const productInfoSection = document.querySelector('[id^="ProductInformation-"]');
    const productDetails = document.querySelector('.product-details');
    const productGrid = document.querySelector('[data-product-grid-content]');
    
    // Use the first available target
    const scrollTarget = productInfoSection || productDetails || productGrid;
    
    if (scrollTarget) {
      // Wait a bit for page to fully render
      setTimeout(() => {
        const headerHeight = this.headerGroup ? this.headerGroup.offsetHeight : 0;
        const targetPosition = scrollTarget.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;
        
        // Smooth scroll to product content
        window.scrollTo({
          top: Math.max(0, targetPosition),
          behavior: 'smooth'
        });
        
        // Mark that we've scrolled
        this.hasScrolled = true;
        this.lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      }, 300);
    }
  }

  setupScrollListener() {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.updateHeaderVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  updateHeaderVisibility() {
    if (!this.header) return;

    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollDelta = currentScrollTop - this.lastScrollTop;
    
    // Determine scroll direction (with a small threshold to avoid jitter)
    const scrollThresholdPx = 5;
    this.isScrollingUp = scrollDelta < -scrollThresholdPx;
    const isScrollingDown = scrollDelta > scrollThresholdPx;
    
    // Only show header if user has scrolled past threshold
    const pastThreshold = currentScrollTop > this.scrollThreshold;
    
    if (this.isScrollingUp && pastThreshold) {
      // User is scrolling up - show header
      this.header.removeAttribute('data-product-page-hidden');
      this.header.dataset.stickyState = 'active';
      this.header.removeAttribute('data-animating');
    } else if (isScrollingDown && currentScrollTop > this.scrollThreshold) {
      // User is scrolling down - hide header
      this.header.setAttribute('data-product-page-hidden', '');
      this.header.setAttribute('data-animating', '');
      
      setTimeout(() => {
        if (this.header && this.header.hasAttribute('data-product-page-hidden')) {
          this.header.dataset.stickyState = 'idle';
          this.header.removeAttribute('data-animating');
        }
      }, 150);
    } else if (currentScrollTop <= this.scrollThreshold) {
      // At top of page - keep header hidden
      this.header.setAttribute('data-product-page-hidden', '');
      this.header.dataset.stickyState = 'idle';
      this.header.removeAttribute('data-animating');
    }
    
    this.lastScrollTop = currentScrollTop;
  }
}

// Initialize on page load
onDocumentLoaded(() => {
  new ProductPageScroll();
});

